import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import ModuleOverlay from '../components/ModuleOverlay';
import { Button, Modal, FormField, EmptyState } from '../components/ui';
import { TAB_ICONS } from '../utils/tabIcons';

const DAY_NAMES = ['Pazar','Pazartesi','Salı','Çarşamba','Perşembe','Cuma','Cumartesi'];

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const map = {
    planned:     { label: 'Planlı',     color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
    in_progress: { label: 'Ziyarette',  color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    completed:   { label: 'Tamamlandı', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
    skipped:     { label: 'Atlandı',    color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
    new_customer:{ label: 'Yeni Müşteri', color: '#0284c7', bg: 'rgba(2,132,199,0.12)' },
  };
  const c = map[status] || map.planned;
  return <span style={{ background: c.bg, color: c.color, padding: '3px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600 }}>{c.label}</span>;
}

export default function PlasiyerDailyPage() {
  const [plasiyers, setPlasiyers] = useState([]);
  const [selectedPlasiyer, setSelectedPlasiyer] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState({ stops: [], visits: [] });
  const [loading, setLoading] = useState(false);
  const [activeVisit, setActiveVisit] = useState(null); // currently editing visit
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchPlasiyers = useCallback(async () => {
    try {
      const r = await api.get('/personnel/plasiyers');
      if (r.data.success) {
        setPlasiyers(r.data.data || []);
        if (r.data.data.length > 0 && !selectedPlasiyer) setSelectedPlasiyer(r.data.data[0].id);
      }
    } catch (e) { console.error(e); }
  }, [selectedPlasiyer]);

  const fetchToday = useCallback(async () => {
    if (!selectedPlasiyer) return;
    setLoading(true);
    try {
      const r = await api.get('/plasiyer/today', { params: { plasiyer_id: selectedPlasiyer, date } });
      if (r.data.success) setData(r.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedPlasiyer, date]);

  useEffect(() => { fetchPlasiyers(); }, [fetchPlasiyers]);
  useEffect(() => { fetchToday(); }, [fetchToday]);

  // Plan + ziyaretleri eşleştir.
  // Birincil eşleştirme: route_stop_id (en doğru).
  // Yedek eşleştirme: account_id — rota sonradan değişmiş ve route_stop_id geçersizse aynı müşteri ziyareti hala görünür.
  const merged = useMemo(() => {
    const visits = data.visits || [];
    const stops = data.stops || [];
    const visitByStopId = {};
    const visitsByAccount = {};
    for (const v of visits) {
      if (v.route_stop_id) visitByStopId[String(v.route_stop_id).toUpperCase()] = v;
      const ak = String(v.account_id).toUpperCase();
      if (!visitsByAccount[ak]) visitsByAccount[ak] = [];
      visitsByAccount[ak].push(v);
    }
    const usedVisitIds = new Set();
    const fromStops = stops.map(s => {
      // Önce route_stop_id ile dene
      let v = visitByStopId[String(s.stop_id).toUpperCase()];
      // Yoksa aynı müşterinin henüz başka stoğa atanmamış ziyaretini al
      if (!v) {
        const list = visitsByAccount[String(s.account_id).toUpperCase()] || [];
        v = list.find(x => !usedVisitIds.has(x.visit_id));
      }
      if (v) usedVisitIds.add(v.visit_id);
      return { stop: s, visit: v || null, key: 'stop-' + s.stop_id };
    });
    const fromOrphans = visits.filter(v => !usedVisitIds.has(v.visit_id)).map(v => ({
      stop: null,
      visit: v,
      key: 'visit-' + v.visit_id,
    }));
    return [...fromStops, ...fromOrphans];
  }, [data]);

  const startVisit = async (item) => {
    let pos = null;
    try {
      pos = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve(null),
          { timeout: 5000 }
        );
      });
    } catch {}

    try {
      if (item.visit) {
        // Var olan ziyareti in_progress'e al
        await api.put('/plasiyer/visits/' + item.visit.visit_id, {
          status: 'in_progress',
          latitude: pos?.lat || null,
          longitude: pos?.lng || null,
        });
      } else {
        // Yeni ziyaret oluştur
        await api.post('/plasiyer/visits', {
          plasiyer_id: selectedPlasiyer,
          account_id: item.stop.account_id,
          route_stop_id: item.stop.stop_id,
          visit_date: date,
          status: 'in_progress',
          latitude: pos?.lat || null,
          longitude: pos?.lng || null,
        });
      }
      await fetchToday();
      setMsg({ type: 'success', text: 'Ziyaret başlatıldı' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    }
  };

  const openVisitDetail = (item) => {
    setActiveVisit(item);
  };

  // Ziyareti iptal et — visit kaydını sil, durak "planlı/yapılmamış" haline döner
  const cancelVisit = async (item) => {
    if (!item.visit?.visit_id) return;
    if (!window.confirm('Ziyaret iptal edilsin mi? Notlar ve görüşme sonucu silinecek, durak yeniden "yapılmamış" olarak görünecek.')) return;
    try {
      await api.delete('/plasiyer/visits/' + item.visit.visit_id);
      setActiveVisit(null);
      await fetchToday();
      setMsg({ type: 'success', text: 'Ziyaret iptal edildi' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    }
  };

  const saveVisitDetail = async (form, finishVisit) => {
    try {
      let visitId = activeVisit?.visit?.visit_id;
      if (!visitId) {
        // Henüz ziyaret oluşturulmamış (planlı) — yeni oluştur
        const r = await api.post('/plasiyer/visits', {
          plasiyer_id: selectedPlasiyer,
          account_id: activeVisit.stop.account_id,
          route_stop_id: activeVisit.stop.stop_id,
          visit_date: date,
          status: finishVisit ? 'completed' : (form.outcome === 'skip' ? 'skipped' : 'in_progress'),
          notes: form.notes,
          outcome: form.outcome,
        });
        visitId = r.data.data.id;
      } else {
        await api.put('/plasiyer/visits/' + visitId, {
          status: finishVisit ? 'completed' : (form.outcome === 'skip' ? 'skipped' : 'in_progress'),
          notes: form.notes,
          outcome: form.outcome,
          end: finishVisit,
        });
      }
      setActiveVisit(null);
      await fetchToday();
      setMsg({ type: 'success', text: finishVisit ? 'Ziyaret kaydedildi' : 'Güncellendi' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    }
  };

  const dayLabel = useMemo(() => {
    const d = new Date(date + 'T12:00:00');
    return DAY_NAMES[d.getDay()];
  }, [date]);

  const stats = useMemo(() => ({
    planned: data.stops?.length || 0,
    completed: (data.visits || []).filter(v => v.status === 'completed').length,
    in_progress: (data.visits || []).filter(v => v.status === 'in_progress').length,
    new_customer: (data.visits || []).filter(v => v.status === 'new_customer').length,
  }), [data]);

  return (
    <div style={{ padding: 16 }}>
      {/* Üstbar */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 14, marginBottom: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
        <div style={{ minWidth: 0 }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Plasiyer</label>
          <select value={selectedPlasiyer} onChange={e => setSelectedPlasiyer(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }}>
            <option value="">Seçiniz</option>
            {plasiyers.map(p => <option key={p.id} value={p.id}>{p.code} — {p.full_name}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Tarih</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }}/>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', alignSelf: 'center', minWidth: 0 }}>
          <strong>{dayLabel}</strong>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
            Plan: {stats.planned} | Tamam: {stats.completed} | Aktif: {stats.in_progress} | Yeni: {stats.new_customer}
          </div>
        </div>
        <Button size="sm" onClick={() => setShowNewCustomer(true)} disabled={!selectedPlasiyer}>+ Yeni Müşteri</Button>
      </div>

      {msg && <div className={'detail-msg ' + msg.type} style={{ marginBottom: 10 }}>{msg.text}</div>}

      {!selectedPlasiyer && (
        <EmptyState
          title={plasiyers.length === 0 ? 'Plasiyer yok' : 'Plasiyer seçin'}
          description={plasiyers.length === 0 ? 'Personel kartında "Plasiyer (Saha)" işaretleyin.' : 'Üstten bir plasiyer seçin'}
        />
      )}

      {selectedPlasiyer && (loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
      ) : merged.length === 0 ? (
        <EmptyState
          title="Planlı durak yok"
          description='Bu gün için planlı durak yok. "Yeni Müşteri" ile plansız ziyaret kaydı oluşturabilirsin.'
        />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {merged.map(item => {
            const account = item.stop || item.visit;
            const status = item.visit?.status || 'planned';
            const code = item.visit?.account_code || item.stop?.account_code;
            const title = item.visit?.account_title || item.stop?.account_title;
            const address = item.stop?.address || item.visit?.address;
            const city = item.stop?.city || item.visit?.city;
            const phone = item.stop?.phone || item.visit?.phone;
            const mobile = item.stop?.mobile || item.visit?.mobile;
            const lat = item.stop?.latitude;
            const lng = item.stop?.longitude;
            const mapsUrl = (lat && lng)
              ? `https://www.google.com/maps?q=${lat},${lng}`
              : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([title, address, city].filter(Boolean).join(' '))}`;

            return (
              <div key={item.key} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 10, padding: 12, display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <strong style={{ fontSize: '0.95rem' }}>{title}</strong>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{code}</span>
                    <StatusBadge status={status} />
                    {item.stop?.planned_time && <span style={{ fontSize: '0.78rem', color: 'var(--accent-primary)', fontFamily: 'monospace' }}>🕒 {item.stop.planned_time}</span>}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    📍 {[address, city].filter(Boolean).join(', ') || 'Adres yok'} {' '}
                    <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>(harita)</a>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {phone && <span>☎ <a href={'tel:' + phone} style={{ color: 'inherit' }}>{phone}</a> </span>}
                    {mobile && <span>📱 <a href={'tel:' + mobile} style={{ color: 'inherit' }}>{mobile}</a></span>}
                    {item.stop?.plan_notes && <div style={{ marginTop: 4, fontStyle: 'italic' }}>📝 {item.stop.plan_notes}</div>}
                  </div>
                  {item.visit && (
                    <div style={{ marginTop: 6, padding: 6, background: 'var(--bg-input)', borderRadius: 6, fontSize: '0.78rem' }}>
                      {item.visit.start_time && <span>Başlangıç: <strong>{fmtTime(item.visit.start_time)}</strong> </span>}
                      {item.visit.end_time && <span>Bitiş: <strong>{fmtTime(item.visit.end_time)}</strong></span>}
                      {item.visit.notes && <div style={{ marginTop: 3 }}>{item.visit.notes}</div>}
                      {item.visit.image_url && (
                        <a href={item.visit.image_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ display: 'inline-block', marginTop: 4 }}>
                          <img src={item.visit.image_url} alt="" style={{ width: 60, height: 60, borderRadius: 6, objectFit: 'cover', border: '1px solid var(--border-secondary)' }}/>
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'center' }}>
                  {(status === 'planned' || !item.visit) && (
                    <Button size="sm" onClick={() => startVisit(item)}>Başlat</Button>
                  )}
                  {(status === 'in_progress' || status === 'completed' || status === 'skipped') && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openVisitDetail(item)}>Detay / Bitir</Button>
                      <Button variant="danger" size="sm" onClick={() => cancelVisit(item)}>İptal</Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Ziyaret detay modalı */}
      {activeVisit && <VisitDetailModal item={activeVisit} onClose={() => setActiveVisit(null)} onSave={saveVisitDetail} onCancel={cancelVisit} />}

      {/* Yeni müşteri modalı */}
      {showNewCustomer && (
        <ModuleOverlay
          type="cari"
          onClose={() => setShowNewCustomer(false)}
          onSaved={async (rec) => {
            setShowNewCustomer(false);
            if (!rec?.id) return;
            // Yeni cari için "new_customer" statüsünde ziyaret oluştur
            try {
              await api.post('/plasiyer/visits', {
                plasiyer_id: selectedPlasiyer,
                account_id: rec.id,
                visit_date: date,
                status: 'new_customer',
                notes: 'Saha ziyaretinde eklenen yeni müşteri',
              });
              await fetchToday();
              setMsg({ type: 'success', text: 'Yeni müşteri eklendi ve ziyaret kaydedildi' });
              setTimeout(() => setMsg(null), 4000);
            } catch (e) {
              setMsg({ type: 'error', text: e.response?.data?.message || e.message });
            }
          }}
        />
      )}
    </div>
  );
}

function VisitDetailModal({ item, onClose, onSave, onCancel }) {
  const [notes, setNotes] = useState(item.visit?.notes || '');
  const [outcome, setOutcome] = useState(item.visit?.outcome || '');
  const [imageUrl, setImageUrl] = useState(item.visit?.image_url || null);
  const [busy, setBusy] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const imgInputRef = React.useRef(null);

  const submit = async (finish) => {
    setBusy(true); setFinishing(finish);
    await onSave({ notes, outcome }, finish);
    setBusy(false);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!item.visit?.visit_id) {
      alert('Resim yüklemek için önce ziyareti kaydedin (📝 Kaydet butonu).');
      return;
    }
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/plasiyer/visits/' + item.visit.visit_id + '/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) setImageUrl(res.data.image_url);
    } catch (e) { alert('Resim yüklenemedi: ' + (e.response?.data?.message || e.message)); }
    finally { setImgUploading(false); if (imgInputRef.current) imgInputRef.current.value = ''; }
  };

  const deleteImage = async () => {
    if (!item.visit?.visit_id) return;
    if (!window.confirm('Resmi silmek istiyor musunuz?')) return;
    try {
      await api.delete('/plasiyer/visits/' + item.visit.visit_id + '/image');
      setImageUrl(null);
    } catch (e) { alert('Resim silinemedi'); }
  };

  // Modal aksiyonları — yeni Modal API: 2-zonlu footer
  // sol-alt: Ziyaret İptal (destructive) — sağ-alt: Kaydet (cancel/secondary) + Ziyareti Bitir (primary)
  const destructiveAction = (item.visit?.visit_id && onCancel)
    ? { label: 'Ziyaret İptal', onClick: () => onCancel(item), disabled: busy }
    : null;
  const cancelAction = { label: 'Kaydet', onClick: () => submit(false), disabled: busy && finishing };
  const primaryAction = { label: 'Ziyareti Bitir', onClick: () => submit(true), loading: busy && finishing, disabled: busy && !finishing };

  return (
    <Modal
      icon={TAB_ICONS.pin}
      title="Ziyaret Detayı"
      subtitle={
        <>
          {(item.visit?.account_title || item.stop?.account_title) && <span>{item.visit?.account_title || item.stop?.account_title}</span>}
          {(item.visit?.visit_date || item.stop?.visit_date) && (
            <>
              <span> · </span>
              <span>{new Date(item.visit?.visit_date || item.stop?.visit_date).toLocaleDateString('tr-TR')}</span>
            </>
          )}
        </>
      }
      size="md"
      onClose={onClose}
      destructiveAction={destructiveAction}
      cancelAction={cancelAction}
      primaryAction={primaryAction}
    >
      <div style={{ marginBottom: 10, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <strong>{item.visit?.account_title || item.stop?.account_title}</strong>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{item.visit?.account_code || item.stop?.account_code}</div>
      </div>
      <FormField label="Sonuç" fullWidth>
        <select value={outcome} onChange={e => setOutcome(e.target.value)}>
          <option value="">— Seçiniz —</option>
          <option value="order">Sipariş alındı</option>
          <option value="visit">Ziyaret tamamlandı</option>
          <option value="follow_up">Takip gerekiyor</option>
          <option value="no_answer">Cevap alınamadı / kapalı</option>
          <option value="skip">Ziyaret atlandı</option>
          <option value="other">Diğer</option>
        </select>
      </FormField>
      <FormField label="Notlar" fullWidth>
        <textarea rows="5" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Görüşme detayı, talep edilen ürünler, şikayetler vs." />
      </FormField>
      <FormField label="Fotoğraf" fullWidth>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {imageUrl ? (
            <div style={{ position: 'relative' }}>
              <a href={imageUrl} target="_blank" rel="noreferrer">
                <img src={imageUrl} alt="" style={{ width: 120, height: 120, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border-secondary)' }}/>
              </a>
              <button type="button" onClick={deleteImage} title="Sil"
                style={{ position: 'absolute', top: -6, right: -6, width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'var(--danger)', color: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
            </div>
          ) : (
            <div style={{ width: 120, height: 120, borderRadius: 8, border: '1px dashed var(--border-secondary)', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>
              Fotoğraf yok
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input ref={imgInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
              onChange={e => uploadImage(e.target.files[0])}/>
            <Button size="sm" disabled={imgUploading} onClick={() => imgInputRef.current?.click()}>
              {imgUploading ? 'Yükleniyor...' : (imageUrl ? 'Değiştir' : 'Çek / Seç')}
            </Button>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
              {item.visit?.visit_id ? 'Telefondan kamera açılır' : 'Önce ziyareti kaydedin'}
            </span>
          </div>
        </div>
      </FormField>
    </Modal>
  );
}
